import { REQUIRED_STARS_TO_ADVANCE } from '../core/scoring';
import { CHEST_COINS, CHEST_SHARDS, type ChestKind, type ChestPrize } from '../config/originChests';
import {
  SHOP_TOWERS,
  SHOP_ORDER,
  WIN_GRANT_TOWERS,
  CHALLENGE_TOWERS,
  EVOLVE_TOWERS,
  type ShopTowerType,
  type EvolveTowerType,
} from '../config/unlocks';
import type { TowerType } from '../core/types';
import { isFeatureFlag, type FeatureFlag } from '../config/featureFlags';
import { AVATAR_ICONS_KEY, isShopAvatarCode } from '../config/avatars';
import { asOrbState, type OrbState } from '../config/varietyOrb';
import {
  asDiamondState,
  asMoonstoneState,
  asEvilOrbState,
  asTokenOfShroomState,
  asLightOrbState,
  type EvilOrbState,
  type TokenOfShroomState,
  type LightOrbState,
  parsePeaceful,
  type DiamondState,
  type MoonstoneState,
  type PeacefulState,
} from '../config/hub';
import {
  activeEffects,
  addEffect,
  canApply,
  heartbeatEffects,
  parseEffects,
  type ApplyCheck,
  type EffectKind,
  type EffectRecord,
} from '../config/effects';
import {
  isPotionKind,
  parsePotionShop,
  parsePotions,
  POTION_SLOTS,
  type PotionKind,
  type PotionShopState,
} from '../config/potions';
import { hubLevelNumber, HUB_LEVELS } from '../config/hubLevels';
import { nextOriginLevel } from '../config/originEvent';
import { LAST_BAG_KEY } from './statsService';
import { FUNGI_ITEMS, type FungiItemKind } from '../config/fungiItems';
import { bumpStat } from '../config/lifetimeStats';

/**
 * Minimal storage contract (a subset of the Web Storage API). Injected so this
 * service stays in the pure layer — no direct browser-global access.
 */
interface StorageLike {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/**
 * ProgressService — owns campaign progression state:
 *   - best star rating per level      (key: 'hambi.stars',       Record<levelId, 0..3>)
 *   - highest unlocked level id        (key: 'hambi.unlocked',    integer ≥ 1)
 *   - episodes entered (first-start)   (key: 'hambi.entered',     JSON number[])
 *   - shop towers owned                (key: 'hambi.owned',       JSON ShopTowerType[])
 *   - win-granted towers               (key: 'hambi.granted',     JSON TowerType[])
 *   - challenge-earned towers          (key: 'hambi.challenge',   JSON TowerType[])
 *   - seen unlock intros               (key: 'hambi.grantedSeen', JSON TowerType[])
 *   - equipped episode loadout         (key: 'hambi.equipped',    JSON TowerType[], ≤10)
 *
 * Unlock rule: clearing a level with ≥ REQUIRED_STARS_TO_ADVANCE stars opens
 * the next level. Level 1 is always unlocked.
 *
 * Cross-chapter exception: level ids listed in `winOnlyGateFrom` advance on a
 * mere win (≥ 1★) rather than the standard gate — used so finishing a chapter's
 * finale opens the next chapter without demanding a perfect run. Since
 * REQUIRED_STARS_TO_ADVANCE is itself 1★ today, this list is currently a no-op;
 * it is kept so the ordinary gate can be tightened again without re-walling the
 * chapter boundaries.
 */
export class ProgressService {
  private readonly STARS_KEY = 'hambi.stars';
  private readonly UNLOCK_KEY = 'hambi.unlocked';
  private readonly COINS_KEY = 'hambi.coins';
  private readonly ENTERED_KEY = 'hambi.entered';
  private readonly OWNED_KEY = 'hambi.owned';
  private readonly GRANTED_KEY = 'hambi.granted';
  private readonly CHALLENGE_KEY = 'hambi.challenge';
  private readonly GRANTED_SEEN_KEY = 'hambi.grantedSeen';
  private readonly EQUIPPED_KEY = 'hambi.equipped';
  private readonly EVOLVED_KEY = 'hambi.evolved';
  /** Epoch ms the pending takeout bag was ordered (absent when none is cooking). */
  private readonly BAG_ORDER_KEY = 'hambi.bagOrderedAt';
  /** Count of ready-but-unopened takeout bags kept in Storage. */
  private readonly BAGS_KEY = 'hambi.bags';
  /** Epoch ms the pending GOLDEN takeout bag was ordered (absent when none is cooking). */
  private readonly GOLDEN_BAG_ORDER_KEY = 'hambi.goldenBagOrderedAt';
  /** Count of ready-but-unopened Golden takeout bags kept in Storage. */
  private readonly GOLDEN_BAGS_KEY = 'hambi.goldenBags';
  /** Dev/admin-granted feature flags (JSON string array; see ../config/featureFlags). */
  private readonly FLAGS_KEY = 'hambi.flags';
  /** Where the one-and-only Variety Orb is: '' | 'stored' | 'opened'. */
  private readonly ORB_KEY = 'hambi.orb';
  // ── The Hub (spec: 2026-09-02-the-hub-design.md) ──
  /** Highest hub level number won (0 = none; Hambi Bar = 1, HTD Exhibition = 2). */
  private readonly HUB_KEY = 'hambi.hub';
  /** Where the one-and-only Diamond is: '' | 'stored' | 'used'. */
  private readonly DIAMOND_KEY = 'hambi.diamond';
  /** Where the one-and-only Moon Stone is: '' | 'stored' | 'used'. */
  private readonly MOONSTONE_KEY = 'hambi.moonstone';
  /** Running timed effects (JSON EffectRecord[]; see ../config/effects). */
  private readonly EFFECTS_KEY = 'hambi.effects';
  /** Potions bought and stored, unused (JSON PotionKind[]). */
  private readonly POTIONS_KEY = 'hambi.potions';
  /** Which Potion Shop slots were bought this cycle (JSON { cycle, sold[] }). */
  private readonly POTION_SHOP_KEY = 'hambi.potionShop';
  /** The Moon's Peaceful track: { on, hub, stars } (spec Revision 6). */
  private readonly PEACEFUL_KEY = 'hambi.hubPeaceful';
  /** Space Debris on the Storage shelf — a COUNT, one per Rocket Rage win. */
  private readonly DEBRIS_KEY = 'hambi.debris';
  /** Golden Stones on the Storage shelf — a COUNT, one per King Hambi K–O. */
  private readonly GOLDSTONE_KEY = 'hambi.goldstone';
  /** Capystones on the Storage shelf — a COUNT, one per finished archive Capy Run. */
  private readonly CAPYSTONE_KEY = 'hambi.capystone';
  /** Best archive Capy Run score (absent until an archive run finishes). */
  private readonly CAPY_BEST_KEY = 'hambi.capyBest';
  private readonly CAPYCOINS_KEY = 'hambi.capycoins';
  /** Dark Hambi Coins — the Origin Event's currency (spec 2026-10-06). */
  private readonly DARK_COINS_KEY = 'hambi.darkCoins';
  /** Dark Shards on the Storage shelf — a COUNT, one per Dark Forest win. */
  private readonly DARK_SHARD_KEY = 'hambi.darkshard';
  /** Light Hambi Coins — Above the Clouds' currency (spec 2026-10-08). */
  private readonly LIGHT_COINS_KEY = 'hambi.lightCoins';
  /** Light Shards on the Storage shelf — a COUNT, one per Sky Islands win. */
  private readonly LIGHT_SHARD_KEY = 'hambi.lightshard';
  /** The Evil Orb: '' | 'stored' (see config/hub EvilOrbState). */
  private readonly EVIL_ORB_KEY = 'hambi.evilorb';
  /** The Token of Shroom: '' | 'stored', the Evil Orb's rule. */
  private readonly TOKEN_OF_SHROOM_KEY = 'hambi.tokenofshroom';
  /** The Light Orb: '' | 'stored' | 'taken' (see config/hub LightOrbState). */
  private readonly LIGHT_ORB_KEY = 'hambi.lightorb';
  /** Origin chests on the Storage shelf — COUNTS (spec 2026-10-13). */
  private readonly DARK_CHEST_KEY = 'hambi.darkchests';
  private readonly LIGHT_CHEST_KEY = 'hambi.lightchests';
  /** Epoch ms the pending capsule was bought (absent when none is spinning). */
  private readonly CAPSULE_ORDER_KEY = 'hambi.capsuleOrderedAt';
  /** Count of ready-but-unopened capsules kept in Storage. */
  private readonly CAPSULES_KEY = 'hambi.capsules';
  /** Max towers fieldable in one episode — also the number of Equip-screen slots. */
  static readonly MAX_EQUIPPED = 10;
  /** Level ids from which a win (≥1★) — not the full 3★ gate — opens the next level. */
  private readonly winGate: Set<number>;

  constructor(
    private readonly storage: StorageLike,
    /** Highest level id in the campaign (clamps the unlock cursor). */
    private readonly maxLevelId: number,
    /** Level ids whose advancement uses the relaxed win-only gate (chapter boundaries). */
    winOnlyGateFrom: Iterable<number> = [],
  ) {
    this.winGate = new Set(winOnlyGateFrom);
  }

  private readStars(): Record<number, number> {
    const raw = this.storage.getItem(this.STARS_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<number, number>;
    } catch {
      return {};
    }
  }

  /** Best star rating (0–3) recorded for a level. */
  stars(levelId: number): number {
    const v = this.readStars()[levelId];
    return typeof v === 'number' ? v : 0;
  }

  /** Highest level id currently unlocked (≥ 1). */
  unlockedThrough(): number {
    const raw = this.storage.getItem(this.UNLOCK_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  /** Whether the given level may be played. */
  isUnlocked(levelId: number): boolean {
    return levelId <= this.unlockedThrough();
  }

  /**
   * Record a finished level: stores the best star rating and, when the earned
   * rating meets the advancement gate, unlocks the next level.
   *
   * Returns `advanced: true` when this result opens a new (next) level —
   * i.e. the gate was met and a next level exists.
   */
  recordResult(levelId: number, starsEarned: number): { best: number; advanced: boolean } {
    const all = this.readStars();
    const best = Math.max(all[levelId] ?? 0, starsEarned);
    all[levelId] = best;
    this.storage.setItem(this.STARS_KEY, JSON.stringify(all));

    // Chapter boundaries advance on a win (≥1★); everywhere else needs the
    // standard gate (also 1★ today — see the class doc).
    const gate = this.winGate.has(levelId) ? 1 : REQUIRED_STARS_TO_ADVANCE;
    const advanced = best >= gate && levelId < this.maxLevelId;
    if (advanced) {
      const next = Math.min(levelId + 1, this.maxLevelId);
      const unlocked = Math.max(this.unlockedThrough(), next);
      this.storage.setItem(this.UNLOCK_KEY, String(unlocked));
    }
    return { best, advanced };
  }

  /**
   * Raise the unlock cursor to (at least) `levelId`, clamped to the campaign.
   * Never lowers progress. Used by the `?debug` "unlock Chapter 2" affordance.
   */
  unlockTo(levelId: number): void {
    const target = Math.max(1, Math.min(Math.floor(levelId), this.maxLevelId));
    const unlocked = Math.max(this.unlockedThrough(), target);
    this.storage.setItem(this.UNLOCK_KEY, String(unlocked));
  }

  /** Dev-granted feature flags held (validated; foreign entries dropped). */
  flags(): FeatureFlag[] {
    try {
      const arr = JSON.parse(this.storage.getItem(this.FLAGS_KEY) ?? '[]') as unknown;
      return Array.isArray(arr) ? arr.filter(isFeatureFlag) : [];
    } catch {
      return [];
    }
  }

  /** Whether a given feature flag is currently granted. */
  hasFlag(flag: FeatureFlag): boolean {
    return this.flags().includes(flag);
  }

  /** Current persistent coin balance (0 if unset or corrupt). */
  coins(): number {
    const raw = this.storage.getItem(this.COINS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /**
   * Add coins (non-negative integer part only); returns the new total. Every
   * coin earned in play also counts toward `hambi.stats.coinsEarned` (the
   * quests); `/give coins` writes the key directly and so does not.
   */
  addCoins(n: number): number {
    const add = Math.max(0, Math.floor(n));
    const total = this.coins() + add;
    this.storage.setItem(this.COINS_KEY, String(total));
    if (add > 0) bumpStat(this.storage, 'coinsEarned', add);
    return total;
  }

  // ── Capycoins (spec: 2026-09-15-capybara-event-design.md) ─────────────────
  // The Capybara Event's own currency: earned by the minigame's score, spent
  // on the capsule. Not coins — never counted toward `coinsEarned`.

  /** Capycoin balance (0 if unset or corrupt). */
  capycoins(): number {
    const raw = this.storage.getItem(this.CAPYCOINS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /** Add Capycoins (non-negative integer part only); returns the new total. */
  addCapycoins(n: number): number {
    const total = this.capycoins() + Math.max(0, Math.floor(n));
    this.storage.setItem(this.CAPYCOINS_KEY, String(total));
    return total;
  }

  /** Spend; false and unchanged when the balance is short (no overdraft). */
  spendCapycoins(n: number): boolean {
    const cost = Math.max(0, Math.floor(n));
    const have = this.capycoins();
    if (have < cost) return false;
    this.storage.setItem(this.CAPYCOINS_KEY, String(have - cost));
    return true;
  }

  // ── Dark Hambi Coins (spec: 2026-10-06-origin-event-design.md) ────────────
  // The Origin Event's own currency: paid by every win of an Origin level.
  // Not coins — never counted toward `coinsEarned`.

  /** Dark Hambi Coin balance (0 if unset or corrupt). */
  darkCoins(): number {
    const raw = this.storage.getItem(this.DARK_COINS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /** Add Dark Hambi Coins (non-negative integer part only); returns the new total. */
  addDarkCoins(n: number): number {
    const total = this.darkCoins() + Math.max(0, Math.floor(n));
    this.storage.setItem(this.DARK_COINS_KEY, String(total));
    return total;
  }

  // Light Hambi Coins (spec 2026-10-08): Above the Clouds pays these, exactly
  // as The Dark Shadows pays Dark Hambi Coins. Not coins either.

  /** Light Hambi Coin balance (0 if unset or corrupt). */
  lightCoins(): number {
    const raw = this.storage.getItem(this.LIGHT_COINS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /** Add Light Hambi Coins (non-negative integer part only); returns the new total. */
  addLightCoins(n: number): number {
    const total = this.lightCoins() + Math.max(0, Math.floor(n));
    this.storage.setItem(this.LIGHT_COINS_KEY, String(total));
    return total;
  }

  /**
   * Record a finished Origin level: the best star rating under its id in
   * `hambi.stars` (ids 201+ never collide). Touches no cursor — the next
   * level's lock reads these stars. `advanced` is true when this result
   * opened the next level in the chapter for the first time.
   */
  recordOriginResult(levelId: number, starsEarned: number): { best: number; advanced: boolean } {
    const all = this.readStars();
    const prev = all[levelId] ?? 0;
    const best = Math.max(prev, starsEarned);
    all[levelId] = best;
    this.storage.setItem(this.STARS_KEY, JSON.stringify(all));
    const advanced = prev < 1 && best >= 1 && nextOriginLevel(levelId) !== undefined;
    return { best, advanced };
  }

  // ── The capsule (spec: 2026-09-16-capy-towers-design.md) ──────────────────
  // The takeout bag's machinery in Capycoins: one capsule spins at a time,
  // its minute is a wall-clock stamp, and a ready one either opens or goes on
  // the Storage shelf.

  /** When the pending capsule was bought, or null when none is spinning. */
  capsuleOrderedAt(): number | null {
    const raw = this.storage.getItem(this.CAPSULE_ORDER_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Buy a capsule: charge `cost` Capycoins and stamp it at `now`. Refuses —
   * changing nothing — when one is already spinning or the balance is short,
   * so the two effects can never be half-applied.
   */
  buyCapsule(cost: number, now: number): boolean {
    if (this.capsuleOrderedAt() != null) return false;
    if (!this.spendCapycoins(cost)) return false;
    this.storage.setItem(this.CAPSULE_ORDER_KEY, String(Math.floor(now)));
    return true;
  }

  /** Clear the pending capsule (it was opened, or moved into Storage). */
  clearCapsuleOrder(): void {
    this.storage.removeItem(this.CAPSULE_ORDER_KEY);
  }

  /** Ready-but-unopened capsules sitting in Storage (0 if unset or corrupt). */
  storedCapsules(): number {
    const raw = this.storage.getItem(this.CAPSULES_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /**
   * The Capybara Event closed on 2026-10-06, and with it the only screen that
   * could OPEN or STORE a capsule still on order. Any such capsule — cooking
   * or ready — goes straight onto the Storage shelf, where it waits until it
   * is opened. Returns true when one moved.
   */
  shelvePendingCapsule(): boolean {
    return this.storeCapsule();
  }

  /** Move the pending (finished) capsule into Storage. No-op when none is. */
  storeCapsule(): boolean {
    if (this.capsuleOrderedAt() == null) return false;
    this.clearCapsuleOrder();
    this.storage.setItem(this.CAPSULES_KEY, String(this.storedCapsules() + 1));
    return true;
  }

  /**
   * Take one capsule out of Storage (to open it). Returns false — changing
   * nothing — when the shelf is empty, so a double-click cannot conjure one.
   */
  takeStoredCapsule(): boolean {
    const n = this.storedCapsules();
    if (n <= 0) return false;
    this.storage.setItem(this.CAPSULES_KEY, String(n - 1));
    return true;
  }

  /** Shop towers purchased so far (validated; corrupt/foreign entries dropped). */
  ownedTowers(): ShopTowerType[] {
    const raw = this.storage.getItem(this.OWNED_KEY);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw) as unknown;
      if (!Array.isArray(arr)) return [];
      return SHOP_TOWERS.filter((t) => arr.includes(t));
    } catch {
      return [];
    }
  }

  /** Whether a shop tower has been purchased. */
  ownsTower(type: ShopTowerType): boolean {
    return this.ownedTowers().includes(type);
  }

  /**
   * Whether the player holds this tower from ANY source — shop purchase, win
   * or dev grant (`/give tower`, `/op`), or challenge prize. The Shop's OWNED
   * label and repurchase guard use this instead of `ownsTower` so an opped
   * player can't pay coins for a tower they already have.
   */
  hasTower(type: ShopTowerType): boolean {
    return this.unlockedExtras().includes(type);
  }

  /**
   * Buy a shop tower with coins. Refuses — returning false and changing
   * nothing — when the tower is already owned, the balance is short, or the
   * cost is negative/non-finite (a caller bug must not become a free tower);
   * otherwise deducts `cost`, records ownership, and returns true. Single
   * method so the check → deduct → record sequence can't be half-applied.
   */
  purchaseTower(type: ShopTowerType, cost: number): boolean {
    if (!Number.isFinite(cost) || cost < 0) return false;
    const price = Math.floor(cost);
    if (this.hasTower(type)) return false;
    const balance = this.coins();
    if (balance < price) return false;
    this.storage.setItem(this.COINS_KEY, String(balance - price));
    this.storage.setItem(this.OWNED_KEY, JSON.stringify([...this.ownedTowers(), type]));
    return true;
  }

  /**
   * Buy a Dark Shop tower with DARK Hambi Coins (spec 2026-10-12) —
   * `purchaseTower`'s contract exactly: refuses (false, nothing changed) when
   * the tower is already held — bought, or handed over by `/give tower` — the
   * balance is short, or the cost is negative/non-finite; otherwise deducts,
   * records ownership under `hambi.owned`, and returns true.
   */
  purchaseTowerWithDarkCoins(type: ShopTowerType, cost: number): boolean {
    if (!Number.isFinite(cost) || cost < 0) return false;
    const price = Math.floor(cost);
    if (this.hasTower(type) || this.grantedTowers().includes(type)) return false;
    const balance = this.darkCoins();
    if (balance < price) return false;
    this.storage.setItem(this.DARK_COINS_KEY, String(balance - price));
    this.storage.setItem(this.OWNED_KEY, JSON.stringify([...this.ownedTowers(), type]));
    return true;
  }

  /**
   * The Light Shop's twin of `purchaseTowerWithDarkCoins` (spec 2026-10-13):
   * the same all-or-nothing contract, on the Light Hambi Coin balance.
   */
  purchaseTowerWithLightCoins(type: ShopTowerType, cost: number): boolean {
    if (!Number.isFinite(cost) || cost < 0) return false;
    const price = Math.floor(cost);
    if (this.hasTower(type) || this.grantedTowers().includes(type)) return false;
    const balance = this.lightCoins();
    if (balance < price) return false;
    this.storage.setItem(this.LIGHT_COINS_KEY, String(balance - price));
    this.storage.setItem(this.OWNED_KEY, JSON.stringify([...this.ownedTowers(), type]));
    return true;
  }

  /** Avatar Shop icons bought, as codes (spec 2026-09-21). */
  ownedAvatarIcons(): string[] {
    try {
      const raw = JSON.parse(this.storage.getItem(AVATAR_ICONS_KEY) ?? '[]') as unknown;
      return Array.isArray(raw) ? raw.filter(isShopAvatarCode) : [];
    } catch {
      return [];
    }
  }

  hasAvatarIcon(code: string): boolean {
    return this.ownedAvatarIcons().includes(code);
  }

  /**
   * Buy an Avatar Shop icon with coins — `purchaseTower`'s contract exactly:
   * refuses on an unknown code, one already owned, a short balance or a bad
   * cost, and otherwise deducts and records in one step so a half-applied
   * purchase cannot charge without delivering.
   */
  purchaseAvatarIcon(code: string, cost: number): boolean {
    if (!isShopAvatarCode(code)) return false;
    if (!Number.isFinite(cost) || cost < 0) return false;
    const price = Math.floor(cost);
    if (this.hasAvatarIcon(code)) return false;
    const balance = this.coins();
    if (balance < price) return false;
    this.storage.setItem(this.COINS_KEY, String(balance - price));
    this.storage.setItem(AVATAR_ICONS_KEY, JSON.stringify([...this.ownedAvatarIcons(), code]));
    return true;
  }

  /**
   * Spend coins. Refuses — returning false and changing nothing — on a
   * negative/non-finite amount or a short balance; otherwise deducts and
   * returns true. The Restaurant's till: `orderBag` is the only caller today.
   */
  spendCoins(n: number): boolean {
    if (!Number.isFinite(n) || n < 0) return false;
    const price = Math.floor(n);
    const balance = this.coins();
    if (balance < price) return false;
    this.storage.setItem(this.COINS_KEY, String(balance - price));
    return true;
  }

  /**
   * Grant a tower outright, with no coins changing hands — how a takeout bag
   * hands one over, and how the `?debug` Phantom Unlock works. (Under `?debug`
   * the whole service is backed by `createSandboxStorage`, so that grant lives
   * for the session only and never reaches the player's save.)
   *
   * Returns true when this NEWLY grants the tower and false when it was already
   * owned. That boolean is the duplicate signal the Restaurant pays its
   * DUPLICATE_REFUND on, so the caller must not also check ownership first —
   * the check and the write belong in one place or a bag could pay out twice.
   */
  awardTower(type: ShopTowerType): boolean {
    if (this.ownsTower(type)) return false;
    this.storage.setItem(this.OWNED_KEY, JSON.stringify([...this.ownedTowers(), type]));
    return true;
  }

  /**
   * Epoch ms at which the pending takeout bag was ordered, or null when the
   * player has no bag cooking. Stored as a wall-clock timestamp so the ten
   * minutes keep running while the game is closed.
   */
  bagOrderedAt(): number | null {
    const raw = this.storage.getItem(this.BAG_ORDER_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * Order a takeout bag: charge `cost` and stamp the order at `now`. Refuses —
   * changing nothing — when a bag is already cooking or the balance is short,
   * so the two effects can never be half-applied.
   */
  orderBag(cost: number, now: number): boolean {
    if (this.bagOrderedAt() != null) return false;
    if (!this.spendCoins(cost)) return false;
    this.storage.setItem(this.BAG_ORDER_KEY, String(Math.floor(now)));
    return true;
  }

  /** Clear the pending order (the bag was opened, or moved into Storage). */
  clearBagOrder(): void {
    this.storage.removeItem(this.BAG_ORDER_KEY);
  }

  /** Ready-but-unopened bags sitting in Storage (0 if unset or corrupt). */
  storedBags(): number {
    const raw = this.storage.getItem(this.BAGS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /** Move the pending (cooked) bag into Storage. No-op when none is pending. */
  storeBag(): boolean {
    if (this.bagOrderedAt() == null) return false;
    this.clearBagOrder();
    this.storage.setItem(this.BAGS_KEY, String(this.storedBags() + 1));
    return true;
  }

  /**
   * Take one bag out of Storage (to open it). Returns false — changing nothing
   * — when Storage is empty, so a double-click can't conjure a free bag.
   */
  takeStoredBag(): boolean {
    const n = this.storedBags();
    if (n <= 0) return false;
    this.storage.setItem(this.BAGS_KEY, String(n - 1));
    return true;
  }

  // ── The Golden Takeout Bag (spec 2026-09-26) ─────────────────────────────
  // The Normal bag's machinery on its own keys: one Golden bag cooks at a
  // time, independently of the Normal one, and a ready one opens or goes on
  // the Storage shelf.

  /** When the pending Golden bag was ordered, or null when none is cooking. */
  goldenBagOrderedAt(): number | null {
    const raw = this.storage.getItem(this.GOLDEN_BAG_ORDER_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  /** Order a Golden bag — the same all-or-nothing contract as `orderBag`. */
  orderGoldenBag(cost: number, now: number): boolean {
    if (this.goldenBagOrderedAt() != null) return false;
    if (!this.spendCoins(cost)) return false;
    this.storage.setItem(this.GOLDEN_BAG_ORDER_KEY, String(Math.floor(now)));
    return true;
  }

  /** Clear the pending Golden order (opened, or moved into Storage). */
  clearGoldenBagOrder(): void {
    this.storage.removeItem(this.GOLDEN_BAG_ORDER_KEY);
  }

  /** Ready-but-unopened Golden bags sitting in Storage (0 if unset or corrupt). */
  storedGoldenBags(): number {
    const raw = this.storage.getItem(this.GOLDEN_BAGS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /** Move the pending (cooked) Golden bag into Storage. No-op when none is pending. */
  storeGoldenBag(): boolean {
    if (this.goldenBagOrderedAt() == null) return false;
    this.clearGoldenBagOrder();
    this.storage.setItem(this.GOLDEN_BAGS_KEY, String(this.storedGoldenBags() + 1));
    return true;
  }

  /** Take one Golden bag out of Storage; false — changing nothing — when empty. */
  takeStoredGoldenBag(): boolean {
    const n = this.storedGoldenBags();
    if (n <= 0) return false;
    this.storage.setItem(this.GOLDEN_BAGS_KEY, String(n - 1));
    return true;
  }

  // ── The Variety Orb ────────────────────────────────────────────────────────
  // One orb exists in the whole game: the prize for beating Level 25. Its
  // three states live in a single key rather than a count plus a claimed flag,
  // because "how many orbs" is never a question that can have another answer.

  /** Where the orb is (anything unrecognised in storage reads as unclaimed). */
  orbState(): OrbState {
    return asOrbState(this.storage.getItem(this.ORB_KEY));
  }

  /**
   * Claim the orb onto the Storage shelf. Returns false — changing nothing —
   * when it has already been claimed, so replaying Level 25 cannot mint a
   * second one and the cutscene has a single source of truth for "show me".
   */
  claimOrb(): boolean {
    if (this.orbState() !== '') return false;
    this.storage.setItem(this.ORB_KEY, 'stored');
    return true;
  }

  /**
   * Mark the orb opened. Returns false when there was no unopened orb, which
   * is what stops a double-click on OPEN from rolling two towers.
   */
  openOrb(): boolean {
    if (this.orbState() !== 'stored') return false;
    this.storage.setItem(this.ORB_KEY, 'opened');
    return true;
  }

  /**
   * Award a tower from the Variety Orb, whichever of the three ownership
   * stores it belongs to. Returns true only when it is NEW.
   *
   * The takeout bag can call `awardTower` directly because all five of its
   * towers are shop-roster ones; the orb's fourteen span the roster, the two
   * win rewards and the arena challenge prize. Each underlying method already
   * refuses out-of-category types and reports newness, so this only has to
   * pick the right one — and, exactly like `awardTower`, the CALLER MUST NOT
   * pre-check ownership: award first, then read the boolean, or a duplicate
   * can pay out twice.
   */
  awardAnyTower(type: TowerType): boolean {
    if (SHOP_TOWERS.includes(type as ShopTowerType)) {
      return this.awardTower(type as ShopTowerType);
    }
    if (WIN_GRANT_TOWERS.includes(type)) return this.grantTower(type);
    if (CHALLENGE_TOWERS.includes(type)) return this.grantChallengeTower(type);
    return false;
  }

  /** Towers permanently Evolved to tier 4 (validated; corrupt/foreign entries dropped). */
  evolvedTowers(): EvolveTowerType[] {
    const raw = this.storage.getItem(this.EVOLVED_KEY);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw) as unknown;
      if (!Array.isArray(arr)) return [];
      return EVOLVE_TOWERS.filter((t) => arr.includes(t));
    } catch {
      return [];
    }
  }

  /** Whether a tower's Evolution has been purchased. */
  hasEvolved(type: EvolveTowerType): boolean {
    return this.evolvedTowers().includes(type);
  }

  /**
   * Buy a tower's Evolution with coins. Same contract as purchaseTower:
   * refuses — returning false and changing nothing — when already evolved,
   * the balance is short, or the cost is negative/non-finite; otherwise
   * deducts `cost`, records the evolution, and returns true. Single method
   * so the check → deduct → record sequence can't be half-applied.
   */
  evolveTower(type: EvolveTowerType, cost: number): boolean {
    if (!Number.isFinite(cost) || cost < 0) return false;
    const price = Math.floor(cost);
    if (this.hasEvolved(type)) return false;
    const balance = this.coins();
    if (balance < price) return false;
    this.storage.setItem(this.COINS_KEY, String(balance - price));
    this.storage.setItem(this.EVOLVED_KEY, JSON.stringify([...this.evolvedTowers(), type]));
    return true;
  }

  /**
   * Granted towers held (validated; corrupt/foreign entries dropped).
   *
   * Validated against the FULL roster (`SHOP_ORDER`), not just
   * `WIN_GRANT_TOWERS` — a dev/admin `/give` can hand out any tower, so a
   * read that only recognized win rewards would silently drop it. `grantTower`
   * itself still only writes win rewards; this widening only affects what a
   * read is willing to keep.
   */
  grantedTowers(): TowerType[] {
    return this.readGrantList(this.GRANTED_KEY, SHOP_ORDER);
  }

  /** Challenge-earned towers held (validated; corrupt/foreign entries dropped). */
  challengeTowers(): TowerType[] {
    return this.readGrantList(this.CHALLENGE_KEY, CHALLENGE_TOWERS);
  }

  /**
   * Award a challenge tower (today: Shrekbook Hambi, for 100 Shrekbruits felled
   * in one Test Arena wave). Returns true only when newly granted — the caller
   * uses that to decide whether to show the unlock overlay — and refuses any
   * type that is not a challenge reward.
   */
  grantChallengeTower(type: TowerType): boolean {
    if (!CHALLENGE_TOWERS.includes(type)) return false;
    if (this.challengeTowers().includes(type)) return false;
    this.storage.setItem(this.CHALLENGE_KEY, JSON.stringify([...this.challengeTowers(), type]));
    return true;
  }

  /**
   * Every tower held OUTSIDE the episode schedule: shop purchases, win rewards
   * and challenge prizes together.
   *
   * This is the `owned` argument every roster site passes to `availableTowers()`
   * — the HUD shop, the arena shelf, the Equip tray, the tower guide and the
   * Evolve gate. It exists as one method precisely so a new grant category
   * reaches all of them at once instead of being spelled out five times.
   */
  unlockedExtras(): TowerType[] {
    return [...this.ownedTowers(), ...this.grantedTowers(), ...this.challengeTowers()];
  }

  /**
   * Grant a win-reward tower (e.g. the Wizard Tower after the Null Nugget).
   * Returns true only when newly granted; refuses non-reward tower types.
   */
  grantTower(type: TowerType): boolean {
    if (!WIN_GRANT_TOWERS.includes(type)) return false;
    if (this.grantedTowers().includes(type)) return false;
    this.storage.setItem(this.GRANTED_KEY, JSON.stringify([...this.grantedTowers(), type]));
    return true;
  }

  /** First granted tower whose unlock intro hasn't been shown yet (null if none). */
  unseenGrantedTower(): TowerType | null {
    const seen = this.readGrantList(this.GRANTED_SEEN_KEY, SHOP_ORDER);
    return this.grantedTowers().find((t) => !seen.includes(t)) ?? null;
  }

  /** Record that a granted tower's unlock intro has been shown (idempotent). */
  markGrantedSeen(type: TowerType): void {
    const seen = this.readGrantList(this.GRANTED_SEEN_KEY, SHOP_ORDER);
    if (seen.includes(type)) return;
    this.storage.setItem(this.GRANTED_SEEN_KEY, JSON.stringify([...seen, type]));
  }

  /**
   * The player's chosen episode loadout, intersected with what they can actually
   * field (`available`), order preserved, capped to MAX_EQUIPPED.
   *
   * Self-configuring default: when no loadout has been saved — or a saved one
   * filters down to nothing — return the first MAX_EQUIPPED of `available` in
   * canonical order. This keeps zero-action players (and freshly earned towers)
   * working without ever opening the Equip screen; once >MAX_EQUIPPED towers are
   * owned, the canonical tail is the one left out until the player curates.
   */
  equippedTowers(available: readonly TowerType[]): TowerType[] {
    const allow = new Set(available);
    const raw = this.storage.getItem(this.EQUIPPED_KEY);
    if (raw != null) {
      try {
        const arr = JSON.parse(raw) as unknown;
        if (Array.isArray(arr)) {
          const seen = new Set<TowerType>();
          const kept: TowerType[] = [];
          for (const t of arr) {
            if (typeof t !== 'string') continue;
            const tt = t as TowerType;
            if (allow.has(tt) && !seen.has(tt)) {
              seen.add(tt);
              kept.push(tt);
            }
            if (kept.length >= ProgressService.MAX_EQUIPPED) break;
          }
          if (kept.length > 0) return kept;
        }
      } catch {
        // fall through to default
      }
    }
    return available.slice(0, ProgressService.MAX_EQUIPPED);
  }

  /**
   * Persist an explicit loadout: dropped to towers in `available`, deduped, and
   * capped to MAX_EQUIPPED (order preserved). Returns the stored list.
   */
  setEquipped(list: readonly TowerType[], available: readonly TowerType[]): TowerType[] {
    const allow = new Set(available);
    const seen = new Set<TowerType>();
    const clean: TowerType[] = [];
    for (const t of list) {
      if (allow.has(t) && !seen.has(t)) {
        seen.add(t);
        clean.push(t);
      }
      if (clean.length >= ProgressService.MAX_EQUIPPED) break;
    }
    this.storage.setItem(this.EQUIPPED_KEY, JSON.stringify(clean));
    return clean;
  }

  /**
   * Read a persisted tower list, filtered to `allow` — so a corrupt value, a
   * hand-edited key or a tower that has since left the category can never put a
   * tower in the player's hands. `allow` also fixes the returned ORDER.
   */
  private readGrantList(key: string, allow: readonly TowerType[]): TowerType[] {
    const raw = this.storage.getItem(key);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw) as unknown;
      if (!Array.isArray(arr)) return [];
      return allow.filter((t) => arr.includes(t));
    } catch {
      return [];
    }
  }

  /**
   * Read the set of level ids the player has ever started.
   *
   * Migration: a pre-feature save has no `hambi.entered` key. In that case treat
   * every already-CLEARED level (id < the current unlock cursor) as entered, so
   * its towers stay available and its tutorial won't replay; the next
   * newly-unlocked but uncleared episode (== the cursor) is still a genuine
   * first start. The seed is materialized to storage on the first markEntered().
   */
  private readEntered(): Set<number> {
    const raw = this.storage.getItem(this.ENTERED_KEY);
    if (raw == null) {
      const seed = new Set<number>();
      for (let id = 1; id < this.unlockedThrough(); id++) seed.add(id);
      return seed;
    }
    try {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr)) {
        return new Set(arr.filter((n): n is number => typeof n === 'number'));
      }
      return new Set();
    } catch {
      return new Set();
    }
  }

  /** Whether the player has ever started this level. */
  hasEntered(levelId: number): boolean {
    return this.readEntered().has(levelId);
  }

  /** Mark a level as started (idempotent, persisted). */
  markEntered(levelId: number): void {
    const set = this.readEntered();
    if (set.has(levelId)) return;
    set.add(levelId);
    this.storage.setItem(this.ENTERED_KEY, JSON.stringify([...set].sort((a, b) => a - b)));
  }

  /** Sorted copy of the entered-level ids. */
  enteredLevels(): number[] {
    return [...this.readEntered()].sort((a, b) => a - b);
  }

  /**
   * Wipe all stars, unlock, coin, entered-episode, and tower progress
   * (Level 1 reverts to the only one open; all tutorials re-arm).
   *
   * This is the only path that also clears the Restaurant's bags — a full wipe
   * really does mean everything, whereas `resetTowers` on its own leaves them
   * (see its doc).
   */
  resetAll(): void {
    this.resetCoins();
    this.resetProgress();
    this.resetTowers();
    this.resetBags();
    this.resetOrb();
    this.resetHub();
  }

  /** Reset the coin balance to zero. */
  resetCoins(): void {
    this.storage.removeItem(this.COINS_KEY);
  }

  /**
   * Reset per-episode campaign progress: best star ratings, the unlock cursor
   * (only Episode I stays open), and which episodes have been entered (so their
   * tutorials re-arm). Leaves coins and owned/granted towers untouched.
   */
  resetProgress(): void {
    this.storage.removeItem(this.STARS_KEY);
    this.storage.removeItem(this.UNLOCK_KEY);
    this.storage.removeItem(this.ENTERED_KEY);
  }

  /**
   * Reset tower access to the starting condition: drop all owned, win-granted
   * and challenge-earned towers (and the seen-intro marks so unlock intros
   * replay), all purchased Evolutions, and the saved episode loadout.
   *
   * Deliberately KEEPS the Restaurant's inventory — a bag on order and every
   * bag in Storage (player-directed, 2026-08-14). A bag is stock you have paid
   * for and not yet opened, so wiping tower ACCESS should not also confiscate
   * it; the bags simply become a way to start rebuilding the roster. Only
   * `resetAll` clears them.
   *
   * Leaves coins and campaign progress untouched.
   */
  resetTowers(): void {
    this.storage.removeItem(this.OWNED_KEY);
    this.storage.removeItem(this.GRANTED_KEY);
    this.storage.removeItem(this.CHALLENGE_KEY);
    this.storage.removeItem(this.GRANTED_SEEN_KEY);
    this.storage.removeItem(this.EQUIPPED_KEY);
    this.storage.removeItem(this.EVOLVED_KEY);
  }

  /** Drop the Restaurant's inventory: any bag on order, and all of Storage. */
  resetBags(): void {
    this.storage.removeItem(this.BAG_ORDER_KEY);
    this.storage.removeItem(this.BAGS_KEY);
    this.storage.removeItem(this.GOLDEN_BAG_ORDER_KEY);
    this.storage.removeItem(this.GOLDEN_BAGS_KEY);
    // The Doubles achievement's last-roll memory goes with the inventory, so
    // a reset (or the next device user) can't score Doubles off an old roll.
    this.storage.removeItem(LAST_BAG_KEY);
  }

  // ── The Hub ───────────────────────────────────────────────────────────────

  /**
   * Highest hub level number won: 0 on a fresh save. `peaceful` reads the
   * Peaceful track's own cursor instead (spec Revision 6).
   */
  hubCompleted(peaceful = false): number {
    if (peaceful) return this.readPeaceful().hub;
    const raw = this.storage.getItem(this.HUB_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 0 ? Math.min(n, HUB_LEVELS.length) : 0;
  }

  /** Raise the hub cursor to (at least) level number `n`. Never lowers it. */
  setHubCompleted(n: number, peaceful = false): void {
    const target = Math.max(0, Math.min(Math.floor(n), HUB_LEVELS.length));
    const next = Math.max(this.hubCompleted(peaceful), target);
    if (peaceful) {
      this.writePeaceful({ ...this.readPeaceful(), hub: next });
      return;
    }
    this.storage.setItem(this.HUB_KEY, String(next));
  }

  /** Best stars for a hub level on the chosen track (Normal shares `hambi.stars`). */
  hubStars(levelId: number, peaceful = false): number {
    if (!peaceful) return this.stars(levelId);
    const v = this.readPeaceful().stars[String(levelId)];
    return typeof v === 'number' ? v : 0;
  }

  /**
   * Record a finished HUB level: stores the best star rating under the hub
   * id (they share `hambi.stars` — ids 101+ never collide with the campaign)
   * and, on a win, raises the hub cursor. Never touches `hambi.unlocked`.
   * `advanced` is true when this win opens a NEXT hub level. `peaceful`
   * records to the Peaceful track instead — its own stars and cursor, the
   * Normal track untouched.
   */
  recordHubResult(
    levelId: number,
    starsEarned: number,
    peaceful = false,
  ): { best: number; advanced: boolean } {
    const n = hubLevelNumber(levelId);
    let best: number;
    if (peaceful) {
      const p = this.readPeaceful();
      best = Math.max(p.stars[String(levelId)] ?? 0, starsEarned);
      this.writePeaceful({ ...p, stars: { ...p.stars, [String(levelId)]: best } });
    } else {
      const all = this.readStars();
      best = Math.max(all[levelId] ?? 0, starsEarned);
      all[levelId] = best;
      this.storage.setItem(this.STARS_KEY, JSON.stringify(all));
    }
    if (n < 1 || best < 1) return { best, advanced: false };
    const before = this.hubCompleted(peaceful);
    this.setHubCompleted(n, peaceful);
    return { best, advanced: n > before && n < HUB_LEVELS.length };
  }

  // ── Peaceful (spec Revision 6) ──

  /** Whether The Moon's picker is showing the Peaceful track. */
  peacefulMode(): boolean {
    return this.readPeaceful().on;
  }

  /** Flip the picker's track. Progress on both tracks is kept either way. */
  setPeacefulMode(on: boolean): void {
    this.writePeaceful({ ...this.readPeaceful(), on });
  }

  private readPeaceful(): PeacefulState {
    return parsePeaceful(this.storage.getItem(this.PEACEFUL_KEY), HUB_LEVELS.length);
  }

  private writePeaceful(p: PeacefulState): void {
    this.storage.setItem(this.PEACEFUL_KEY, JSON.stringify(p));
  }

  /** Where the Diamond is (anything unrecognised reads as unclaimed). */
  diamondState(): DiamondState {
    return asDiamondState(this.storage.getItem(this.DIAMOND_KEY));
  }

  /** Claim the Diamond onto the Storage shelf; false when already claimed. */
  claimDiamond(): boolean {
    if (this.diamondState() !== '') return false;
    this.storage.setItem(this.DIAMOND_KEY, 'stored');
    return true;
  }

  /** Mark the Diamond used; false when there was no stored diamond. */
  useDiamond(): boolean {
    if (this.diamondState() !== 'stored') return false;
    this.storage.setItem(this.DIAMOND_KEY, 'used');
    return true;
  }

  // ── The Moon Stone (spec: 2026-09-11-through-space-and-time) ─────────────
  // The Diamond's twin: one exists ever, paid for the FIRST win of the
  // chapter finale, and thirty minutes of +50% damage on every tower.

  /** Where the Moon Stone is (anything unrecognised reads as unclaimed). */
  moonstoneState(): MoonstoneState {
    return asMoonstoneState(this.storage.getItem(this.MOONSTONE_KEY));
  }

  /** Claim the Moon Stone onto the Storage shelf; false when already claimed. */
  claimMoonstone(): boolean {
    if (this.moonstoneState() !== '') return false;
    this.storage.setItem(this.MOONSTONE_KEY, 'stored');
    return true;
  }

  /** Mark the Moon Stone used; false when there was no stored stone. */
  useMoonstone(): boolean {
    if (this.moonstoneState() !== 'stored') return false;
    this.storage.setItem(this.MOONSTONE_KEY, 'used');
    return true;
  }

  // ── Space Debris (spec: 2026-09-10-nacho-stages-design.md) ────────────────
  // Unlike the Diamond, of which one exists ever, debris is a STACK: Rocket
  // Rage pays one for every win, so the shelf holds a count.

  /** Space Debris sitting in Storage (0 if unset or corrupt). */
  storedDebris(): number {
    const raw = this.storage.getItem(this.DEBRIS_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Pay one piece of debris onto the shelf (a Rocket Rage win). */
  awardDebris(): number {
    const n = this.storedDebris() + 1;
    this.storage.setItem(this.DEBRIS_KEY, String(n));
    return n;
  }

  /**
   * Take one piece off the shelf (to use it). Returns false — changing nothing
   * — when the shelf is empty, so a double-click cannot conjure a free one.
   */
  takeDebris(): boolean {
    const n = this.storedDebris();
    if (n <= 0) return false;
    this.storage.setItem(this.DEBRIS_KEY, String(n - 1));
    return true;
  }

  // ── The Golden Stone (spec: 2026-09-22-king-hambi-design.md) ─────────────
  // The Moon Stone in gold, and a STACK like debris: King Hambi pays one for
  // every K–O he casts, so the shelf holds a count.

  /** Golden Stones sitting in Storage (0 if unset or corrupt). */
  storedGoldstones(): number {
    const raw = this.storage.getItem(this.GOLDSTONE_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Pay Golden Stones onto the shelf (default one — a K–O). */
  awardGoldstone(count = 1): number {
    const n = this.storedGoldstones() + Math.max(0, Math.floor(count));
    this.storage.setItem(this.GOLDSTONE_KEY, String(n));
    return n;
  }

  /** Take one stone off the shelf; false (changing nothing) when it is empty. */
  takeGoldstone(): boolean {
    const n = this.storedGoldstones();
    if (n <= 0) return false;
    this.storage.setItem(this.GOLDSTONE_KEY, String(n - 1));
    return true;
  }

  // ── The Event Archive (spec: 2026-10-04-archive-and-skip-design.md) ──────
  // The Capystone is the Golden Stone in capybara brown: a count on the shelf,
  // one paid for every archive Capy Run that reaches its end.

  /** Capystones sitting in Storage (0 if unset or corrupt). */
  storedCapystones(): number {
    const raw = this.storage.getItem(this.CAPYSTONE_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Pay Capystones onto the shelf (default one — a finished archive run). */
  awardCapystone(count = 1): number {
    const n = this.storedCapystones() + Math.max(0, Math.floor(count));
    this.storage.setItem(this.CAPYSTONE_KEY, String(n));
    return n;
  }

  /** Take one Capystone off the shelf; false (changing nothing) when empty. */
  takeCapystone(): boolean {
    const n = this.storedCapystones();
    if (n <= 0) return false;
    this.storage.setItem(this.CAPYSTONE_KEY, String(n - 1));
    return true;
  }

  /** The best archive Capy Run score, or null when no archive run has finished. */
  capyBest(): number | null {
    const raw = this.storage.getItem(this.CAPY_BEST_KEY);
    const n = raw != null ? Number(raw) : NaN;
    return Number.isInteger(n) && n >= 0 ? n : null;
  }

  /**
   * Record a finished archive run. The stored value is the HIGH score — a
   * worse run never lowers it. Returns whether this run set a new best (the
   * first finished run always does, even at 0).
   */
  recordCapyScore(score: number): boolean {
    const s = Math.max(0, Math.floor(Number.isFinite(score) ? score : 0));
    const prev = this.capyBest();
    if (prev != null && prev >= s) return false;
    this.storage.setItem(this.CAPY_BEST_KEY, String(s));
    return true;
  }

  // ── The Dark Shadows, levels 3–5 (spec 2026-10-07) ──────────────────────
  // The Dark Shard is a count on the shelf, one per Dark Forest win; the Evil
  // Orb is a one-of, paid on the first win of its quest, and does nothing yet.

  /** Dark Shards sitting in Storage (0 if unset or corrupt). */
  storedDarkShards(): number {
    const raw = this.storage.getItem(this.DARK_SHARD_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Pay Dark Shards onto the shelf (default one — a Dark Forest win). */
  awardDarkShard(count = 1): number {
    const n = this.storedDarkShards() + Math.max(0, Math.floor(count));
    this.storage.setItem(this.DARK_SHARD_KEY, String(n));
    return n;
  }

  /** Take one Dark Shard off the shelf; false (changing nothing) when empty. */
  takeDarkShard(): boolean {
    const n = this.storedDarkShards();
    if (n <= 0) return false;
    this.storage.setItem(this.DARK_SHARD_KEY, String(n - 1));
    return true;
  }

  /**
   * The Fungi Forest's items on the Storage shelf (spec 2026-10-24): a COUNT
   * per kind under its own synced key (0 if unset or corrupt).
   */
  storedFungiItems(kind: FungiItemKind): number {
    const raw = this.storage.getItem(FUNGI_ITEMS[kind].storageKey);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Put Fungi items on the shelf (a drop, a gift, a cancelled pick). */
  awardFungiItem(kind: FungiItemKind, count = 1): number {
    const n = this.storedFungiItems(kind) + Math.max(0, Math.floor(count));
    this.storage.setItem(FUNGI_ITEMS[kind].storageKey, String(n));
    return n;
  }

  /** Take one off the shelf; false (changing nothing) when there is none. */
  takeFungiItem(kind: FungiItemKind): boolean {
    const n = this.storedFungiItems(kind);
    if (n <= 0) return false;
    this.storage.setItem(FUNGI_ITEMS[kind].storageKey, String(n - 1));
    return true;
  }

  /** Light Shards sitting in Storage (0 if unset or corrupt). */
  storedLightShards(): number {
    const raw = this.storage.getItem(this.LIGHT_SHARD_KEY);
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Pay Light Shards onto the shelf (default one — a Sky Islands win). */
  awardLightShard(count = 1): number {
    const n = this.storedLightShards() + Math.max(0, Math.floor(count));
    this.storage.setItem(this.LIGHT_SHARD_KEY, String(n));
    return n;
  }

  /** Take one Light Shard off the shelf; false (changing nothing) when empty. */
  takeLightShard(): boolean {
    const n = this.storedLightShards();
    if (n <= 0) return false;
    this.storage.setItem(this.LIGHT_SHARD_KEY, String(n - 1));
    return true;
  }

  evilOrbState(): EvilOrbState {
    return asEvilOrbState(this.storage.getItem(this.EVIL_ORB_KEY));
  }

  /**
   * Put the Evil Orb on the shelf. True when it was not there already — the
   * cue for the claim modal. The CALLER gates it on the first win.
   */
  claimEvilOrb(): boolean {
    if (this.evilOrbState() === 'stored') return false;
    this.storage.setItem(this.EVIL_ORB_KEY, 'stored');
    return true;
  }

  tokenOfShroomState(): TokenOfShroomState {
    return asTokenOfShroomState(this.storage.getItem(this.TOKEN_OF_SHROOM_KEY));
  }

  /**
   * Put the Token of Shroom on the shelf. True when it was not there already —
   * the cue for the claim modal. The CALLER gates it on the first win.
   */
  claimTokenOfShroom(): boolean {
    if (this.tokenOfShroomState() === 'stored') return false;
    this.storage.setItem(this.TOKEN_OF_SHROOM_KEY, 'stored');
    return true;
  }

  lightOrbState(): LightOrbState {
    return asLightOrbState(this.storage.getItem(this.LIGHT_ORB_KEY));
  }

  /**
   * Put the Light Orb on the shelf. True only when it had never been held —
   * the cue for the claim modal. Called on EVERY Heaven's Peak win; it is the
   * state, not the stars, that makes it pay once.
   */
  claimLightOrb(): boolean {
    if (this.lightOrbState() !== '') return false;
    this.storage.setItem(this.LIGHT_ORB_KEY, 'stored');
    return true;
  }

  private chestKey(kind: ChestKind): string {
    return kind === 'dark' ? this.DARK_CHEST_KEY : this.LIGHT_CHEST_KEY;
  }

  /** Dark or Light Chests sitting in Storage (0 if unset or corrupt). */
  storedChests(kind: ChestKind): number {
    const raw = this.storage.getItem(this.chestKey(kind));
    const n = raw != null ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /** Put chests on the shelf (default one — a lucky Origin win). */
  awardChest(kind: ChestKind, count = 1): number {
    const n = this.storedChests(kind) + Math.max(0, Math.floor(count));
    this.storage.setItem(this.chestKey(kind), String(n));
    return n;
  }

  /** Take one chest off the shelf; false (changing nothing) when there is none. */
  takeChest(kind: ChestKind): boolean {
    const n = this.storedChests(kind);
    if (n <= 0) return false;
    this.storage.setItem(this.chestKey(kind), String(n - 1));
    return true;
  }

  /**
   * Pay what a chest held (spec 2026-10-13): two shards onto the shelf, or 50
   * of the chapter's coins. Call AFTER `takeChest` and BEFORE the animation, so
   * a closed tab still keeps the prize.
   */
  payChestPrize(kind: ChestKind, prize: ChestPrize): void {
    if (prize === 'shards') {
      if (kind === 'dark') this.awardDarkShard(CHEST_SHARDS);
      else this.awardLightShard(CHEST_SHARDS);
    } else if (kind === 'dark') this.addDarkCoins(CHEST_COINS);
    else this.addLightCoins(CHEST_COINS);
  }

  /**
   * The open game's heartbeat for effects that pause while it is closed (the
   * Dark Shard; see config/effects `heartbeatEffects`). Writes only when such
   * an effect is in the list. Returns whether it wrote.
   */
  heartbeatEffects(now: number): boolean {
    const next = heartbeatEffects(this.effects(), now);
    if (!next) return false;
    this.setEffects(next, now);
    return true;
  }

  /** Every persisted effect record, expired ones included (see `activeEffects`). */
  effects(): EffectRecord[] {
    return parseEffects(this.storage.getItem(this.EFFECTS_KEY));
  }

  /** Persist a full effect list (expired records are dropped as of `now`). */
  setEffects(list: readonly EffectRecord[], now: number): void {
    this.storage.setItem(this.EFFECTS_KEY, JSON.stringify(activeEffects(list, now)));
  }

  /**
   * Start an effect at `now` if the stacking rules allow it. Returns the
   * `canApply` verdict; on `ok` the record is persisted.
   */
  startEffect(kind: EffectKind, tower: TowerType | undefined, now: number): ApplyCheck {
    const list = this.effects();
    const check = canApply(list, kind, tower, now);
    if (!check.ok) return check;
    this.setEffects(addEffect(list, kind, tower, now), now);
    return check;
  }

  /** Potions bought and stored for later, in purchase order. */
  storedPotions(): PotionKind[] {
    return parsePotions(this.storage.getItem(this.POTIONS_KEY));
  }

  /** Put a bought potion on the shelf. */
  storePotion(kind: PotionKind): void {
    if (!isPotionKind(kind)) return;
    const list = this.storedPotions();
    list.push(kind);
    this.storage.setItem(this.POTIONS_KEY, JSON.stringify(list));
  }

  /** Take the stored potion at `index` off the shelf; null if there is none. */
  takeStoredPotion(index: number): PotionKind | null {
    const list = this.storedPotions();
    if (index < 0 || index >= list.length) return null;
    const [kind] = list.splice(index, 1);
    this.storage.setItem(this.POTIONS_KEY, JSON.stringify(list));
    return kind;
  }

  /** The Potion Shop's SOLD record (null = nothing recorded). */
  potionShop(): PotionShopState | null {
    return parsePotionShop(this.storage.getItem(this.POTION_SHOP_KEY));
  }

  /** Mark slot `slot` of `cycle` bought. A different cycle's record is replaced. */
  markPotionSold(cycle: number, slot: number): void {
    const cur = this.potionShop();
    const sold =
      cur && cur.cycle === cycle
        ? cur.sold.slice(0, POTION_SLOTS)
        : Array.from({ length: POTION_SLOTS }, () => false);
    if (slot >= 0 && slot < POTION_SLOTS) sold[slot] = true;
    this.storage.setItem(this.POTION_SHOP_KEY, JSON.stringify({ cycle, sold }));
  }

  /**
   * Drop every Hub key: progress, the Diamond, the Moon Stone, Space Debris,
   * running effects and potions.
   */
  resetHub(): void {
    this.storage.removeItem(this.HUB_KEY);
    this.storage.removeItem(this.PEACEFUL_KEY);
    this.storage.removeItem(this.DIAMOND_KEY);
    this.storage.removeItem(this.MOONSTONE_KEY);
    this.storage.removeItem(this.DEBRIS_KEY);
    this.storage.removeItem(this.GOLDSTONE_KEY);
    this.storage.removeItem(this.CAPYSTONE_KEY);
    this.storage.removeItem(this.DARK_COINS_KEY);
    this.storage.removeItem(this.DARK_SHARD_KEY);
    this.storage.removeItem(this.EVIL_ORB_KEY);
    this.storage.removeItem(this.TOKEN_OF_SHROOM_KEY);
    this.storage.removeItem(this.LIGHT_COINS_KEY);
    this.storage.removeItem(this.LIGHT_SHARD_KEY);
    this.storage.removeItem(this.LIGHT_ORB_KEY);
    this.storage.removeItem(this.DARK_CHEST_KEY);
    this.storage.removeItem(this.LIGHT_CHEST_KEY);
    this.storage.removeItem(FUNGI_ITEMS.shroom.storageKey);
    this.storage.removeItem(FUNGI_ITEMS.snowshroom.storageKey);
    this.storage.removeItem(this.EFFECTS_KEY);
    this.storage.removeItem(this.POTIONS_KEY);
    this.storage.removeItem(this.POTION_SHOP_KEY);
  }

  /**
   * Un-claim the Variety Orb, so beating Level 25 awards it again.
   *
   * Only `resetAll` calls this, for the same reason `resetBags` is `resetAll`-
   * only: an unopened orb is a prize already won, and wiping tower ACCESS or
   * campaign progress should not confiscate it. The consequence is deliberate
   * — after a progress-only reset the campaign can be replayed but the orb is
   * not re-awarded, because it has already been given once.
   */
  resetOrb(): void {
    this.storage.removeItem(this.ORB_KEY);
  }
}
